import {
  Category,
  Service,
  TCFData,
  TCFSpecialFeature,
  TCFUserDecisions,
  UserDecision,
} from '@usercentrics/cmp-browser-sdk';

export type ScrollToId = string | number;
/**
 * Generates a Service array from the Categories array and returns it
 * @param categories the array of categories
 */
const generateServicesFromCategories = (categories: Category[]): Service[] => {
  return categories.reduce((result: Service[], category: Category) => {
    return result.concat(category.services);
  }, []);
};

/**
 * Returns user decisions based on the categories
 * @param newCategories the new categories
 * before EUD-1079-amp-reject fix: (newCategories: Category[], oldCategories: Category[])
 */
export const getUserDecision = (newCategories: Category[]): UserDecision[] => {
  // newServices represents the current toggle states in the UI.
  const newServices = generateServicesFromCategories(newCategories);
  // oldServices represents the last stored consents from the localstorage.
  // const oldServices = generateServicesFromCategories(oldCategories);

  // Returns a diff between newServices and oldServices consents. It contains the consents for the
  // services that have recently been toggled in the UI but not yet stored to the localstorage.
  return newServices.reduce((result: UserDecision[], newService) => {
    // const matchingOldService = oldServices.find((oldService) => oldService.id === newService.id) as Service;

    // if (newService.consent.status !== matchingOldService.consent.status) {
    result.push({
      serviceId: newService.id,
      status: newService.consent.status,
    });
    // }

    return result;
  }, []);
};

const nullishOperation = (element: boolean | undefined | null, altElement: boolean): boolean => {
  return element != null ? element : altElement;
};

/**
 * Returns TCF user decisions based on TCF data
 * @param tcfData the tcf data
 */
export const getTCFUserDecision = (tcfData: TCFData): TCFUserDecisions => {
  return {
    purposes: tcfData.purposes.map((purpose) => ({
      consent: purpose.consent || false,
      id: purpose.id,
      legitimateInterestConsent: purpose.showLegitimateInterestToggle
        ? nullishOperation(purpose.legitimateInterestConsent, true)
        : false,
    })),
    specialFeatures: tcfData.specialFeatures.map((specialFeature: TCFSpecialFeature) => ({
      consent: specialFeature.consent || false,
      id: specialFeature.id,
    })),
    vendors: tcfData.vendors.map((vendor) => ({
      consent: vendor.consent || false,
      id: vendor.id,
      legitimateInterestConsent: vendor.legitimateInterestPurposes.length
        ? nullishOperation(vendor.legitimateInterestConsent, true)
        : false,
    })),
  };
};

/**
 * Map the choices of the current UI state to the new (translated) categories/services.
 * @param newCategories the new categories
 * @param oldCategories the old caterogies
 */
export const getCurrentUiCategoriesChoices = (newCategories: Category[], oldCategories: Category[]): Category[] => {
  return newCategories.map((newCategory) => {
    const oldCategory = oldCategories.find((category) => newCategory.slug === category.slug);

    const services = newCategory.services.map((newService: Service) => {
      const { consent } = oldCategory?.services.find((service: Service) => newService.id === service.id) || newService;
      const updatedService = newService;
      updatedService.consent = consent;

      return updatedService;
    });

    return { ...newCategory, services };
  });
};
